import React, { useCallback, useEffect, useRef, useState, ChangeEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import { Button, ButtonWithIcon, ButtonWithLoading, Content, Icon, PageHeader, SelectField, SmallInput, Switch, useModal, useNotification, useMediaModal } from 'scorer-ui-kit';
import { getAdvancedLowSpeed, getCamera, setAdvancedLowSpeed, toggleConfigurationAnalysis } from 'services/camerasService';
import styled, {css} from 'styled-components';
import { IVector2, IPointSet, LineUIOptions } from 'scorer-ui-kit/dist/LineUI';
import LineViewer from 'components/LineViewer/configurationLineViewer';
import { IExclusionAreas } from 'pages/CameraConfiguration/types';
import { NEW_SET } from '../../constants';
import { IAdvancedLowSpeedDetectionData, IAdvancedLowSpeedDetails, ICameraDetails, ILines, IAdvancedLowSpeedDetectionDataMapped, IAdvancedLowSpeedDetailsMapped, ILinesMapped } from 'interface';
import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import i18n from 'i18n';
import DisableConfirmationModal from 'components/DisableConfirmationModal';
import {IToggleAnalysisPayload} from '../../interface';
import ImportSettingsModal from './ConfigurationImportModal';
import { EDGE_API_BASE_URL, ENABLE_ADAVANCE_LOW_SPEED_ANALYSIS } from '../../constants';
import axios from 'axios';

const MainContainer = styled(Content)`
  overflow: unset;
  @media screen and (min-width: 1440px) {
    padding: 60px 90px 60px 170px;
  }
  @media (max-width: 1400px) and (min-width: 1350px) {
    padding: 70px 0 70px 90px;
  }
`;

const Container = styled.div`
  width: 100%;
  max-width: 940px !important;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
  max-width: 941px !important;
  justify-content: space-between;
  margin-top: -22px;
  padding: 0;
  p {
    margin-top: -14px;
  }
`;

const PageHeaderDiv = styled.div`
  display: flex;
  flex-direction: column;
  > div > div {
    > h1 {
      margin-left: -1px;
    }
    > :nth-child(2) {
      bottom: 2px;
      left: -45px;
    }
  }
`;

const AreaContainer = styled.div`
  min-width: 200px;
  max-width: 750px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 10px;
  font-family: ${({ theme }) => theme.fontFamily.data};
  font-size: 12px;
  font-weight: 500;
  color: #767676;
  cursor: pointer;
`;

const HeadTitle = styled.span`
  & div > div > div > div {
    margin-top: -8px;
  }
`;

const ToggleDetection = styled.div<{ state?: string }>`
  display: flex;
  align-items: center;
  margin-top: -4px;
  gap: 0 12px;
  ${({ state }) =>
    state === 'disabled' &&
    css`
      pointer-events: none;
    `}
  margin-bottom: 15px;
`;

const LineLabel = styled.div`
  font-family: ${({ theme }) => theme.fontFamily.ui};
  font-size: 14px;
  font-weight: 500;
  color: #8b9196;
`;

const SwitchLabel = styled(LineLabel)`
  margin-left: 14px;
`;

const ButtonContainer = styled.div`
  margin-top: -1px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 10px 0;
  > button {
    width: 133px;
  }
  > button:nth-child(1) {
    background: #e4edf4;
    &:hover:enabled {
      background: #d4e4f0;
    }
  }
  & > :nth-child(2) {
    width: 135px;
    margin-right: -2px;
    padding: 0 18px;
  }
`;

const UnsavedChangesContainer = styled.div`
  width: 100%;
  font-size: 12px;
  text-align: center;
  font-family: ${({ theme }) => theme.fontFamily.ui};
`;

const Divider = styled.div`
  height: 1px;
  margin-bottom: 30px;
  border-radius: 3px;
  background-color: #efefef;
`;

const ImportExportContainer = styled.div<{ gap: string; lang?: string }>`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 0 11px;
  margin-top: -15px;
  margin-bottom: 15px;

  > button {
    padding: 0 8px;
    > div > :first-child {
      padding-left: 12px;
    }
    background: #e4edf4;
    :hover:enabled {
      background: #d4e4f0;
    }
    :disabled {
      background: #e9f0f6;
    }
  }
`;

const LineSettingsContainer = styled.div`
  width: 100%;
  max-width: 940px !important;
  margin: 4px 0;
`;

const LineSelectFieldContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0 10px;
  position: relative;
  & > div:first-child {
    display: flex;
    position: absolute;
    top: 6px;
    left: -24px;
  }
`;

const StyledSelectField = styled(SelectField)`
  width: 229px;
  background: #fff;
  &:disabled {
    opacity: 0.7;
  }
  & + div {
    top: 50% !important;
    transform: translateY(-50%) !important;
  } // to align drop down icon
`;

const InputAndButtonContainer = styled.div<{ gap: string; lang?: string }>`
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: ${({ gap }) => gap};
  & > button {
    background: #e4edf4;
    :hover:enabled {
      background: #d4e4f0;
    }
    :disabled {
      background: #e9f0f6;
    }
  }
`;

const LineUIContainer = styled.div`
  width: 100%;
  min-height: 390px;
  margin: 30px 0 15px;
  display: flex;
  border-radius: 3px;
  border: solid 1px #eee;
  box-shadow: 0 4px 9px 0 rgba(152, 174, 189, 0.02);
  background-color: rgba(252, 252, 253, 0.85);
  position: relative;

  &::before {
    content: "";
    width: 14.5px;
    height: 51px;
    position: absolute;
    top: -28px;
    left: -16px;
    border-left: 2px solid #d9dad9;
    border-bottom: 2px solid #d9dad9;
  }
`;

const LineUIRightPanel = styled.div`
  flex: 1;
  flex-shrink: 0;
  border-radius: 3px;
`;

const LineUILeftPanel = styled.div`
  padding: 15px 0;
  width: 239px;
  flex-shrink: 0;
  position: relative;
`;

const SidePaddingDiv = styled.div`
  position: relative;
  padding: 0 19px;
  & > div > label > span {
    margin-bottom: 4px !important;
  }
`;

interface ISmallInput {
  length: string;
}

const StyledSmallInputWithLabel = styled(SmallInput)<ISmallInput>`
  & > label {
    width: ${({ length }) => length};
    margin-bottom: 0;
  }
`;

const DividerWithMargin = styled(Divider)`
  margin: 12px 0 12px;
`;

const InputDiv = styled.div`
  padding: 10px 20px 0 19px;
  height: 80px;
  margin-bottom: 3px;
  & > div > label > div > input {
    width: 135px !important;
  }
  & > div > label > span {
    margin-bottom: 4px !important;
  }
`;

const LowSpeedThreshold = styled.div`
  padding: 0 20px 0 19px;
  display: flex;
  justify-content: space-between;
  & > div > label > div {
    width: 89px !important;
  }
  & > div > label > span {
    margin-bottom: 4px !important;
  }
  & > div > label > div > div {
    right: 6px !important;
    top: 1px !important;
  }
`;

const CompareSpeedContainer = styled.div<{ lang: string }>`
  padding: 0px 36px 0 5px;
  display: flex;
  justify-content: space-between;
  width: ${({ lang }) => (lang === 'ja' ? '215px' : '')};
  & > div > label > div {
    width: 89px !important;
  }
  & > div > label > span {
    margin-bottom: 4px !important;
  }
`;

const SwitchContainer = styled.span`
  width: 50px;
  & > label {
    padding-left: 14px;
  }
`;

const CompareSpeed = styled.div`
  margin-top: -2px;
  > div {
    margin-top: 2px;
  }
  > span > label > div {
    margin-top: 2px;
  }
`;

const DecreaseLavel = styled.div`
  position: relative;
  top: 1px;
  height: 16px;
  padding: 0 20px 0 19px;
  margin-bottom: 10px;
  font-family: ${({ theme }) => theme.fontFamily.ui};
  font-size: 14px;
  font-weight: bold;
  color: #8b9196;
`;

const CancelModalContainer = styled.div`
  padding: 18px 48px 22px 48px;
  display: flex;
  flex-direction: column;
  row-gap: 20px;
`;

const CancelConfirmText = styled.p`
  margin: 0;
  padding: 0;
  font-size: 14px;
  font-weight: 500;
  line-height: 1.79;
  color: #7c7e7f;
`;

const ModalButtonContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  column-gap: 9px;
  & > button:nth-child(1) {
    background: #e4edf4;
    &:hover:enabled {
      background: #d4e4f0;
    }
  }
`;

const CancelChangesButton = styled(Button)`
  &:focus {
    outline: 2px solid #838383;
  }
`;

const LineUIActionsDisplayDropdown = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 396.5px;
  height: max-content;
  & > div:nth-child(1) {
    margin-top: -4px;
  }
  & > div:nth-child(2) {
    margin-top: -15px;
  }
  position: absolute;
  z-index: 10;
  bottom: -34px;
`;

const IconWrapper = styled.div`
  padding-top: 4px;
`;

const SelectZoneShow = styled(SelectField)`
  width: 138px;
  height: 30px !important;
  background-color: transparent !important;
  border: none !important;
  padding: 0 25px 0 8px !important;
  font-style: normal !important;
  margin-top: 9px;
  & > div > select {
  }
  & > div > div {
    margin-top: -8px !important;
  }
`;

type key = 'lineReference' | 'threshold' | 'distance' | 'level1' | 'level2';

interface Iline {
    x: number,
    y: number
  }
  
interface ILineSets {
  index: number,
  line_set_name: string,
  lines: {
    line1: Iline[]
    line2: Iline[]
    line1_styling?: string
    line2_styling?: string
  },
}

const options: LineUIOptions = {
  showSetIndex: false,
  showPoint: true,
  showDirectionMark: false,
  showMoveHandle: false,
  showPointHandle: true,
  setIndexOffset: 0,
  boundaryOffset: 0
};

interface IImageResponse {
  data: ArrayBuffer,
  status: number,
}

interface ILinePoints {
  points: IVector2[],
  changeLineStyle: boolean
}

type AdvLowSpeedRes = IAdvancedLowSpeedDetectionDataMapped;
type AdvLowSpeedKeys = keyof AdvLowSpeedRes;
type LineKeys = keyof IAdvancedLowSpeedDetailsMapped;
type PointsKeys = keyof ILinesMapped;
type CheckTypeValue = number | string | boolean | IAdvancedLowSpeedDetailsMapped[] | ILinesMapped | undefined;

const checkType = (key: string, value: CheckTypeValue): boolean => {
  switch (key) {
  case 'advanced_low_speed':
    return Array.isArray(value);
  case 'name':
    return typeof value === 'string';
  case 'distance_meters':
  case 'speed_decrease_level1_percent':
  case 'speed_decrease_level2_percent':
    return typeof value === 'number';
  case 'compare_speed_to_last_hour':
  case 'compare_speed_to_last_5min':
    return typeof value === 'boolean';
  case 'lines':
    return true;
  case 'x0':
  case 'y0':
  case 'x1':
  case 'y1':
    return typeof value === 'number';
  default:
    return false;
  }
};

const AdvancedLowSpeed = () => {
  const { t } = useTranslation(['CommonDict']);
  const { streamName }: { streamName: string } = useParams();
  const [cameraData, setCameraData] = useState<ICameraDetails>();
  const { sendNotification } = useNotification();
  const sendNotificationRef = useRef(sendNotification);
  const { push } = useHistory();
  const [setOfLines , setSetOfLines] = useState<IPointSet[]>([]);
  const { createModal, setModalOpen, isModalOpen } = useModal();
  const [selectedSet, setSelectedSet] = useState<string>(NEW_SET);
  const [selectedSetsIndex, setSelectedSetsIndex] = useState<number>(0);
  const [lowSpeedData, setLowSpeedData] = useState<IAdvancedLowSpeedDetectionData>({enabled: false, advanced_low_speed_settings: []});
  const [exclusionAreas, setExclusionAreas] = useState<IExclusionAreas>({enabled: false, areas: []});
  const [prevValuesForCompare, setPrevValuesForCompare] = useState<IAdvancedLowSpeedDetectionData>({enabled: false, advanced_low_speed_settings: []});
  const [prevLowSpeedData, setPrevLowSpeedData] = useState<IAdvancedLowSpeedDetectionData>({enabled: false, advanced_low_speed_settings: []});
  const [lowSpeedSetsSettings, setLowSpeedSetsSettings] = useState<IAdvancedLowSpeedDetails[]>([]);
  const [lowSpeedSelectOptions, setLowSpeedSelectOptions] = useState<string[]>([NEW_SET]);
  const [unSavedChanges, setUnsavedChanges] = useState<number>(0);
  const [lineSets, setLineSets] = useState<ILineSets[]>([]);
  const [lowSpeedSaveLoading, setLowSpeedSaveLoading] = useState(false);
  const [selectZoneOption, setSelectZoneOption] = useState('Show All');
  const [showAllZone, setShowAllZone] = useState(false);
  const tRef = useRef(t);
  const [previousData, setPreviousData] = useState(true);
  const [isMediaLoaded, setIsMediaLoaded] = useState(false);
  const [toggleAnalysis, setToggleAnalysis] = useState(false);
  const stateRef = useRef<IPointSet[]>([]);
  const [imageDetails, setImageDetails] = useState({x: 1920, y: 1080});
  const { isMediaUrlValid } = useMediaModal();
  const [image, setImage] = useState('');
  const [isImporting, setIsImporting] = useState(true);

  const setChangesCounter = (prevDetails: IAdvancedLowSpeedDetectionData, currentDetails: IAdvancedLowSpeedDetectionData) => {
    let counter = 0;
    
    // If any Sets has length 0
    if (currentDetails?.advanced_low_speed_settings.length === 0 || prevDetails?.advanced_low_speed_settings.length === 0) {
      counter = counter + currentDetails?.advanced_low_speed_settings.length + prevDetails?.advanced_low_speed_settings.length;
      return counter;
    }

    // count set difference
    currentDetails?.advanced_low_speed_settings.forEach((lane1) => {
      let found = 1;
      for (const lane2 of prevDetails?.advanced_low_speed_settings) {
        if (isEqual(lane1, lane2)) {
          found = 0;
          break;
        }
      }
      counter += found;
    });
    
    if (currentDetails?.advanced_low_speed_settings.length < prevDetails?.advanced_low_speed_settings.length) {
      counter += Math.abs(currentDetails?.advanced_low_speed_settings.length - prevDetails?.advanced_low_speed_settings.length);
    }

    return counter;
  };

  useEffect(()=>{
    const currentExclusionAreaSettings = lowSpeedData?.advanced_low_speed_settings.map(obj => {
      const newObj = { ...obj };
      return newObj;
    });
    const prevExclusionAreaSettings = prevValuesForCompare?.advanced_low_speed_settings.map(obj => {
      const newObj = { ...obj };
      return newObj;
    });
    const prevSettings = {
      ...prevValuesForCompare,
      advanced_low_speed_settings: prevExclusionAreaSettings
    };

    const currentDetails:IAdvancedLowSpeedDetectionData = {enabled: lowSpeedData?.enabled, advanced_low_speed_settings: currentExclusionAreaSettings};
    let changes = 0;
    changes = setChangesCounter(prevSettings, currentDetails);
    setUnsavedChanges(changes);
  },[prevValuesForCompare, lowSpeedData]);

  const handleLowSpeed = useCallback(async()=>{
    try {
      const res = await getAdvancedLowSpeed(streamName);
      if (res.data.status_code === 0 && res.data.data ) {
        if (res.data.exclusion_areas) {
          const {enabled: enabledExclusion, exclusion_zone_settings = []} = res.data.exclusion_areas;
          setExclusionAreas({
            enabled: enabledExclusion,
            areas: exclusion_zone_settings.map(area => ({points: area.points}))
          });
        }
        setLowSpeedData(res.data.data);
        if(previousData){
          setPrevValuesForCompare(res.data.data);
        }
        setPrevLowSpeedData(cloneDeep(res.data.data));
        setLowSpeedSetsSettings(res.data.data?.advanced_low_speed_settings);
        setSelectedSet(NEW_SET);
        
        const areaSelect = res.data.data?.advanced_low_speed_settings.map(item => item?.line_set_name);
        areaSelect.unshift(NEW_SET);
        setSelectedSetsIndex(0);
        setLowSpeedSelectOptions(areaSelect);
        const lines: ILineSets[] = [];
        res.data.data.advanced_low_speed_settings.map((i:IAdvancedLowSpeedDetails) => {
          const newLinesSets = {
            index: i.index,
            lines: i.lines,
            line_set_name: i?.line_set_name,
          };
          lines.push(newLinesSets);
          return i;
        });
        setLineSets(lines);
        setPreviousData(false);
      } else {
        sendNotificationRef.current({type: 'error', message: tRef.current('Failed to communicate with the system')});
      }
    } catch (error) {
      console.error(error);
    }
  },[streamName, previousData]);

  useEffect(()=>{
    const state: IPointSet[] = [];
    lineSets.map((line: ILineSets) => {
      const lines1: IPointSet = {
        name: line?.line_set_name,
        points: line.lines.line1,
        styling: line.lines.line1_styling === 'danger' ? 'danger' : (line.index === 1 ? 'primary' : 'secondary'),
        showPointHandle: (line.index === selectedSetsIndex)
      };
      const lines2: IPointSet = {
        name: line?.line_set_name,
        points: line.lines.line2,
        styling: line.lines.line2_styling === 'danger' ? 'danger' : (line.index === 1 ? 'primary' : 'secondary'),
        showPointHandle: (line.index === selectedSetsIndex)
      };
      state.push(lines1);
      state.push(lines2);
      return line;
    });
    if(lowSpeedData?.advanced_low_speed_settings.length === 0){
      setSetOfLines([]);
    }else{
      setSetOfLines(state);
      stateRef.current = state;
    }
  },[lineSets, lowSpeedData, selectedSet, selectedSetsIndex]);

  const fetchCamera = useCallback(() => {
    getCamera(streamName).then((res) => {
      if (res.status === 200 && res.data.data) {
        setCameraData(res.data.data);
      } else {
        sendNotificationRef.current({ type: 'error', message: tRef.current('Failed to communicate with the system')});
      }
    }).catch(() => {
      sendNotificationRef.current({ type: 'error', message: tRef.current('Failed to communicate with the system')});
    });
  }, [streamName]);

  useEffect(()=>{
    if (ENABLE_ADAVANCE_LOW_SPEED_ANALYSIS) {
      fetchCamera();
      handleLowSpeed();
    } else {
      push('/cameras/camera-configuration/' + streamName);
    }
  },[streamName, fetchCamera, handleLowSpeed, push]);

  const onLineSelect = useCallback((e: string) => {
    setSelectedSet(e);
    if (e === NEW_SET || e === '［新しいセット］') {
      setSelectedSetsIndex(0);
    } else {
      lowSpeedSetsSettings.forEach((item)=>{
        if(item?.line_set_name === e){
          setSelectedSetsIndex(item.index);
        }
      });
    }
  },[lowSpeedSetsSettings]);

  const onChangeLowSpeedData = useCallback((formData) => {
    setLowSpeedData({ ...formData });
  }, []);

  const onLineChange = useCallback((linesData: IPointSet[])=>{
    if (selectedSetsIndex === 0) return;
    const newLowSpeedData = cloneDeep(lowSpeedData);
    const selectedLines = linesData.filter(line => line.showPointHandle);

    const line1: ILines[] = [];
    const line2: ILines[] = [];
    selectedLines.forEach((line, index) => {
      if (index % 2 === 0) {
        line1.push(...line.points.map(point => ({ x: point.x, y: point.y })));
      } else {
        line2.push(...line.points.map(point => ({ x: point.x, y: point.y })));
      }
    });
    newLowSpeedData.advanced_low_speed_settings[selectedSetsIndex - 1] = {
      ...newLowSpeedData.advanced_low_speed_settings[selectedSetsIndex - 1],
      lines: {
        line1,
        line2,
        line1_styling: selectedLines[0].styling,
        line2_styling: selectedLines[1].styling
      },
    };
    
    onChangeLowSpeedData({ ...newLowSpeedData });
  },[selectedSetsIndex, lowSpeedData, onChangeLowSpeedData]);

  const onLineClickCallback = useCallback((lineId: number) => {
    const lineName = stateRef.current[lineId].name === undefined ? '1' : stateRef.current[lineId].name;
    const setIndex = lowSpeedData?.advanced_low_speed_settings.findIndex((lane) => (lane.line_set_name === lineName));
    setSelectedSetsIndex(setIndex+1);
    setSelectedSet(lowSpeedData?.advanced_low_speed_settings[setIndex]?.line_set_name);
    onLineSelect(lowSpeedData?.advanced_low_speed_settings[setIndex]?.line_set_name);
  },[lowSpeedData, onLineSelect]);

  const onChangeSets = useCallback((key: key) => ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    const newLowSpeedDataData = cloneDeep(lowSpeedData);
    if(key === 'lineReference'){
      const newAreaSelectOptions = [...lowSpeedSelectOptions];
      newAreaSelectOptions[selectedSetsIndex] = value;
      setLowSpeedSelectOptions(newAreaSelectOptions);
      setSelectedSet(value);
      if(value !== undefined){
        newLowSpeedDataData.advanced_low_speed_settings[selectedSetsIndex - 1].line_set_name = value;
      }
    } else if(key === 'distance'){
      if(parseInt(value) === 1000){
        value = '999';
      }
      const t = value;
      value = (t.indexOf('.') >= 0) ? (t.substr(0, t.indexOf('.')) + t.substr(t.indexOf('.'), 3)) : t;
      if(value.length > 6){
        return;
      }else if(value === '' || /^\d{0,3}(\.\d{0,2})?$/.test(value)){
        newLowSpeedDataData.advanced_low_speed_settings[selectedSetsIndex - 1].distance = parseFloat(value.replace(/[^0-9.]/gi, ''));
      }
    } else if(key === 'level1'){
      const t = value;
      value = (t.indexOf('.') >= 0) ? (t.substr(0, t.indexOf('.')) + t.substr(t.indexOf('.'), 3)) : t;
      if(value.length > 5){
        return;
      }else if(parseFloat(value) > 100){
        return;
      }else if(value === '' || /^\d{0,3}(\.\d{0,2})?$/.test(value)){
        newLowSpeedDataData.advanced_low_speed_settings[selectedSetsIndex - 1].level1_speed_decrease_threshold = parseFloat(value);
      }
    } else if(key === 'level2'){
      const t = value;
      value = (t.indexOf('.') >= 0) ? (t.substr(0, t.indexOf('.')) + t.substr(t.indexOf('.'), 3)) : t;
      if(value.length > 5){
        return;
      }else if(parseFloat(value) > 100){
        return;
      }else if(value === '' || /^\d{0,3}(\.\d{0,2})?$/.test(value)){
        newLowSpeedDataData.advanced_low_speed_settings[selectedSetsIndex - 1].level2_speed_decrease_threshold = parseFloat(value);
      }
    } 
    onChangeLowSpeedData(newLowSpeedDataData); 
  }, [lowSpeedData, lowSpeedSelectOptions, onChangeLowSpeedData, selectedSetsIndex]);

  const onRemoveSets = useCallback(() => {
    const removeData = setOfLines.filter((item)=> {return item.name !== selectedSet;});
    setSetOfLines(removeData);
    const updatedData = {
      ...lowSpeedData,
      advanced_low_speed_settings: lowSpeedData?.advanced_low_speed_settings.filter(
        (item) => item.index !== selectedSetsIndex
      ).map((item, index) => ({ ...item, index: index + 1 }))
    };
    setLowSpeedSetsSettings(updatedData.advanced_low_speed_settings);
    setSelectedSetsIndex(updatedData.advanced_low_speed_settings.length);
    setSelectedSet(NEW_SET);
    onLineSelect(NEW_SET);
    const remZoneSelectOptions = updatedData?.advanced_low_speed_settings.map(item => item?.line_set_name);
    remZoneSelectOptions.unshift(NEW_SET);
    setLowSpeedSelectOptions(remZoneSelectOptions);
    onChangeLowSpeedData(updatedData); 
    sendNotificationRef.current({ type: 'neutral', message: tRef.current('Set removed'), icon: 'FeatureLineUi'});
  },[setOfLines, lowSpeedData, onLineSelect, onChangeLowSpeedData, selectedSet, selectedSetsIndex]);

  const onAddSets = useCallback(() => {
    const defaultData: IAdvancedLowSpeedDetails = {
      index: lowSpeedSetsSettings.length + 1,
      styling: lowSpeedSetsSettings.length === 0 ? 'primary' : 'secondary',
      line_set_name: lowSpeedSetsSettings.length === 0 ?  i18n.language === 'ja' ? ('セット ' + (lowSpeedSetsSettings.length + 1)) : ('Set ' + (lowSpeedSetsSettings.length + 1)) : i18n.language === 'ja' ? (lowSpeedSetsSettings[0]?.line_set_name === 'セット ' + (lowSpeedSetsSettings.length + 1) ?'セット 1' : 'セット ' + (lowSpeedSetsSettings.length + 1)) : (lowSpeedSetsSettings[0]?.line_set_name === 'Set ' + (lowSpeedSetsSettings.length + 1) ?'Set 1' : 'Set ' + (lowSpeedSetsSettings.length + 1)) ,
      lines: {
        line1: [
          {
            x: lowSpeedSetsSettings.length === 1 ? 1097 : 736,
            y: lowSpeedSetsSettings.length === 1 ? 230 : 97
          },
          {
            x: lowSpeedSetsSettings.length === 1 ? 1094 : 758,
            y: lowSpeedSetsSettings.length === 1 ? 527 : 613
          }
        ],
        line2: [
          {
            x: lowSpeedSetsSettings.length === 1 ? 428 : 215,
            y: lowSpeedSetsSettings.length === 1 ? 228 : 109
          },
          {
            x: lowSpeedSetsSettings.length === 1 ? 420 : 218,
            y: lowSpeedSetsSettings.length === 1 ? 532 : 607
          }
        ]
      },
      direction: 'East',
      distance: 20,
      level1_speed_decrease_threshold: 10,
      level2_speed_decrease_threshold: 20,
      level1_enabled: true,
      level2_enabled: true
    };
    setSelectedSet(defaultData?.line_set_name);
    onLineSelect( defaultData?.line_set_name);
    setSelectedSetsIndex(defaultData.index);
    
    const lines: ILineSets[] = [];
    const low:IAdvancedLowSpeedDetails[] = [...lowSpeedSetsSettings];
    
    low.push(defaultData);
    setLowSpeedSetsSettings(low);
    low.map((i:IAdvancedLowSpeedDetails) => {
      const newLinesSets = {
        index: i.index,
        lines: i.lines,
        line_set_name: i?.line_set_name,
      };
      lines.push(newLinesSets);
      const areaSelect = low.map(item => item?.line_set_name);
      areaSelect.unshift(NEW_SET);
      setLowSpeedSelectOptions(areaSelect);
      return i;
    });
    
    setLineSets(lines);
    setLowSpeedData({enabled: lowSpeedData?.enabled, advanced_low_speed_settings: low});
    sendNotificationRef.current({type: 'neutral', message: tRef.current('New set added'), icon: 'FeatureLineUi'});
  },[lowSpeedSetsSettings, onLineSelect, lowSpeedData]);

  const onMediaLoadedCallback = useCallback(() => {
    setIsMediaLoaded(true);
  }, []);

  const handleCancel = useCallback(async (setModalOpen: (newStatus: boolean) => void) => {
    setSelectedSetsIndex(0);
    await setModalOpen(false);
    if(prevLowSpeedData) {
      setLowSpeedData(prev => ({...prevLowSpeedData, enabled: prev.enabled}));
      setPrevValuesForCompare(prev => ({...prevLowSpeedData, enabled: prev.enabled}));
      setLowSpeedSetsSettings(prevLowSpeedData.advanced_low_speed_settings);
      const zoneSelect = prevLowSpeedData.advanced_low_speed_settings.map(area => area.line_set_name);
      zoneSelect.unshift(NEW_SET);
      setLowSpeedSelectOptions(zoneSelect);
      setSelectedSetsIndex(0);
      setSelectedSet(NEW_SET);
    }
  }, [prevLowSpeedData]);

  const arrowEvent = useCallback((e) => {
    if (isModalOpen) {
      const validKeys = ['ArrowLeft', 'ArrowRight', 'Tab'];
      if (!validKeys.includes(e.key)) return;
      e.preventDefault();

      if (e.key === 'ArrowLeft') {
        document.getElementById('CancelButton')?.focus();
      } else if (e.key === 'ArrowRight') {
        document.getElementById('YesButton')?.focus();
      } else if (e.key === 'Escape') {
        document.getElementById('CancelButton')?.click();
      } else if (e.key === 'Tab') {
        if (document.activeElement?.id === 'CancelButton') {
          document.getElementById('YesButton')?.focus();
          return;
        }
        document.getElementById('CancelButton')?.focus();
      }
    }
  }, [isModalOpen]);

  useEffect(() => {
    window.addEventListener('keydown', arrowEvent);
    if (isModalOpen) {
      document.getElementById('CancelButton')?.focus();
    }
    return () => {
      window.removeEventListener('keydown', arrowEvent);
    };
  }, [isModalOpen, arrowEvent]);

  const handleCancleNo = useCallback(() =>{
    setModalOpen(false);
  },[setModalOpen]);

  const getCancelModal = useCallback((setModalOpen: (newStatus: boolean) => void) => {
    return (
      <CancelModalContainer>
        <CancelConfirmText>{tRef.current('Are you sure you want to cancel the changes?')}</CancelConfirmText>
        <ModalButtonContainer>
          <CancelChangesButton id='CancelButton' design='secondary' onClick={() => handleCancleNo()}>{tRef.current('No')}</CancelChangesButton>
          <CancelChangesButton id='YesButton' onClick={() => handleCancel(setModalOpen)}>{tRef.current('Yes')}</CancelChangesButton>
        </ModalButtonContainer>
      </CancelModalContainer>
    );
  }, [handleCancel, handleCancleNo]);

  const handleAnalysis = useCallback((value)=>{
    setToggleAnalysis(true);
    const newLowSpeedData = cloneDeep(lowSpeedData);
    newLowSpeedData.enabled = value;
    onChangeLowSpeedData(newLowSpeedData);

    const payload: IToggleAnalysisPayload ={
      action: value ? 'enable' : 'disable',
      analysis_type: ['advanced_low_speed_detection']
    };
    toggleConfigurationAnalysis(streamName, payload).then((res: { data: { status_code: number; }; }) => {
      if (res.data.status_code === 0) {
        sendNotificationRef.current({ type: 'success', message: value ? tRef.current('Analysis enabled successfully') : tRef.current('Analysis disabled successfully')});
        setToggleAnalysis(false);
      } else { 
        newLowSpeedData.enabled = !value;
        onChangeLowSpeedData(newLowSpeedData);
        sendNotificationRef.current({ type: 'error', message: tRef.current('Failed to communicate with the system') });
        setToggleAnalysis(false); }
    }).catch(() => {
      newLowSpeedData.enabled = !value;
      onChangeLowSpeedData(newLowSpeedData);
      setToggleAnalysis(false);
      sendNotificationRef.current({type: 'error', message: tRef.current('Failed to communicate with the system')});
    });
  },[lowSpeedData, streamName, onChangeLowSpeedData]);

  const handleLastHour = useCallback((value)=>{
    const newLowSpeedData = cloneDeep(lowSpeedData);
    if(newLowSpeedData !== undefined){
      newLowSpeedData.advanced_low_speed_settings[selectedSetsIndex - 1].level1_enabled = value;
    }
    onChangeLowSpeedData(newLowSpeedData);
  },[lowSpeedData, onChangeLowSpeedData, selectedSetsIndex]);

  const handleLastFiveHour = useCallback((value)=>{
    const newLowSpeedData = cloneDeep(lowSpeedData);
    if(newLowSpeedData !== undefined){
      newLowSpeedData.advanced_low_speed_settings[selectedSetsIndex - 1].level2_enabled = value;
    }
    onChangeLowSpeedData(newLowSpeedData);
  },[lowSpeedData, onChangeLowSpeedData, selectedSetsIndex]);

  const validateExclusionZoneData = useCallback((lowSpeedData) => {
    let returnValue = true;
    for (const item of lowSpeedData.advanced_low_speed_settings) {
      if (item?.line_set_name === '') {
        sendNotificationRef.current({type: 'error', message: tRef.current('Please enter set reference for set #{X}').replace('{X}', (item.index))});
        returnValue = false;
        break;
      }
      if (isNaN(item?.distance)) {
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Line distance should be > 0 for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      }
      if(item?.distance > 999.99 ){
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Line distance should be > 0 and <= 99.99 km/h for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      } 
      if( item?.distance <= 0){
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Line distance should be > 0 and <= 99.99 km/h for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      } 
      if (isNaN(item?.level1_speed_decrease_threshold) && isNaN(item?.level2_speed_decrease_threshold)) {
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Speed decrease level 1 and level 2 should be > 0 for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      }
      if (isNaN(item?.level1_speed_decrease_threshold)) {
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Speed decrease level 1 should be > 0 for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      }
      if (isNaN(item?.level2_speed_decrease_threshold)) {
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Speed decrease level 2 should be > 0 for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      }
      if(item?.level1_speed_decrease_threshold > 100 ){
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Speed decrease level 1 and level 2 should be > 0 for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      } 
      if( item?.level1_speed_decrease_threshold <= 0){
        sendNotificationRef.current({ type: 'error', message: `${tRef.current('Speed decrease level 1 and level 2 should be > 0 for set #{X}').replace('{X}', (item.index))}` });
        returnValue = false;
        break;
      } 
      if(lowSpeedData.advanced_low_speed_settings.length === 2){
        if(lowSpeedData.advanced_low_speed_settings[0]?.line_set_name === lowSpeedData.advanced_low_speed_settings[1]?.line_set_name){
          sendNotificationRef.current({type: 'error', message: tRef.current('{X} already exists').replace('{X}', (item?.line_set_name))});
          returnValue = false;
        }
      }
    }
    return returnValue;
  }, []);

  const onClickSave = useCallback(async(data = lowSpeedData) => {
    const isValidData = validateExclusionZoneData(data);
    if(isValidData){
      onChangeLowSpeedData({ ...lowSpeedData });
      setLowSpeedSaveLoading(true);
      try {
        const res = await setAdvancedLowSpeed(streamName, data);
        if(res.data.status_code === 0){
          setPrevLowSpeedData(cloneDeep(lowSpeedData));
          sendNotificationRef.current({ type: 'success', message: tRef.current('Settings saved successfully')});
          setPreviousData(true);
          fetchCamera();
          handleLowSpeed();
        } else { sendNotificationRef.current({ type: 'error', message: tRef.current('Failed to communicate with the system') }); }
        setLowSpeedSaveLoading(false);
      }catch (error) {
        console.error(error);
        setLowSpeedSaveLoading(false);
        sendNotificationRef.current({type: 'error', message: tRef.current('Failed to communicate with the system')});
      }
    }
    
  }, [handleLowSpeed, lowSpeedData, validateExclusionZoneData, onChangeLowSpeedData, streamName, fetchCamera]);

  const handleZoneShow = useCallback((e) => {
    setShowAllZone(!showAllZone);
    setSelectZoneOption(e);
  },[showAllZone]);

  useEffect(()=>{
    const lines: ILineSets[] = [];
    if(selectZoneOption === 'Only Active Set'){
      lowSpeedData?.advanced_low_speed_settings.forEach((item)=>{
        if(item?.line_set_name === selectedSet){
          const newLinesSets = {
            index: item.index,
            lines: item.lines,
            line_set_name: item?.line_set_name,
          };
          lines.push(newLinesSets);
        }
        setLineSets(lines);
      });
    } else{
      lowSpeedData?.advanced_low_speed_settings.forEach((item)=>{
        const newLinesSets = {
          index: item.index,
          lines: item.lines,
          line_set_name: item?.line_set_name,
        };
        lines.push(newLinesSets);
        setLineSets(lines);
      });
    }
  },[lowSpeedData, selectZoneOption, selectedSet]);

  const handleKeyDownDistance = (event:React.KeyboardEvent<HTMLInputElement>) => {
    const inputElement = event.target as HTMLInputElement;
    // Check if the key pressed is a dot
    const decimalPart = inputElement.value.split('.')[1];
    if(decimalPart !== undefined){
      if(event.key === '0' && decimalPart.charAt(1)==='0'){
        event.preventDefault();
      }
    }
    if (event.key === '.' && inputElement.value.charAt(0)==='' ) {
      event.preventDefault();
    }
    if (event.key === '-' || event.key === '+'  || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  };

  const handleKeyDownLevel = (event:React.KeyboardEvent<HTMLInputElement>) => {
    const inputElement = event.target as HTMLInputElement;
    // Check if the key pressed is a dot
    const decimalPart = inputElement.value.split('.')[1];
    if(decimalPart !== undefined){
      if(event.key === '0' && decimalPart.charAt(1)==='0'){
        event.preventDefault();
      }
    }
    if (event.key === '.' && inputElement.value.charAt(0)==='' ) {
      event.preventDefault();
    }
    if (event.key === '.' && inputElement.value==='100' ) {
      event.preventDefault();
    }
    if (event.key === '-' || event.key === '+'  || event.key === 'e' || event.key === 'E') {
      event.preventDefault();
    }
  };

  const goToPreviousPage = useCallback(() =>{
    if(isEqual(lowSpeedData.advanced_low_speed_settings, prevValuesForCompare.advanced_low_speed_settings)){
      push('/cameras/camera-configuration/' + streamName);
      return;
    }
    const CancelModal: ReactElement = getCancelModal(setModalOpen);
    createModal({ isCloseEnable: false, width: '400px', padding: false, customComponent: CancelModal });
  },[createModal, getCancelModal, push, streamName, lowSpeedData, prevValuesForCompare, setModalOpen]);

  const diableCallBack = useCallback((isEnabled: boolean) => {
    setModalOpen(false);
    onClickSave({...lowSpeedData, enabled: isEnabled});
  }, [lowSpeedData, setModalOpen, onClickSave]);

  const openConfirmationModal = useCallback(() => {
    if (lowSpeedData.enabled) {
      onClickSave(lowSpeedData);
    } else {
      const isValidData = validateExclusionZoneData(lowSpeedData);
      if(isValidData){
        const ConfirmationModal: ReactElement = <DisableConfirmationModal onCallback={diableCallBack} />;
        createModal({ isCloseEnable: false, width: 'auto', padding: false, customComponent: ConfirmationModal });
      }
    }
  }, [lowSpeedData, validateExclusionZoneData, createModal, onClickSave, diableCallBack]);

  const onExportSettings = useCallback(() => {
    const { advanced_low_speed_settings } = lowSpeedData;
    const exportLines = advanced_low_speed_settings.map(({ index, styling, line_set_name, distance, level1_speed_decrease_threshold, level2_speed_decrease_threshold, level1_enabled, level2_enabled, direction, lines, ...rest }) => {
      const linesMapped: ILinesMapped[] = [
        {
          x0: lines.line1[0].x,
          y0: lines.line1[0].y,
          x1: lines.line1[1].x,
          y1: lines.line1[1].y,
        },
        {
          x0: lines.line2[0].x,
          y0: lines.line2[0].y,
          x1: lines.line2[1].x,
          y1: lines.line2[1].y,
        },
      ];
      return { ...rest, name: line_set_name, distance_meters: distance, speed_decrease_level1_percent: level1_speed_decrease_threshold, speed_decrease_level2_percent: level2_speed_decrease_threshold, compare_speed_to_last_hour: level1_enabled, compare_speed_to_last_5min: level2_enabled, lines: linesMapped };
    });
    try {
      let modifiedCameraName = cameraData?.camera_name || '';
      if (modifiedCameraName.length > 50) {
        modifiedCameraName = modifiedCameraName.substring(0, 50) + '...';
      }
      const fileName = `${modifiedCameraName}_advanced_lowspeed.json`;
      const dataStr = 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify({ advanced_low_speed: exportLines }, null, 4));
      const link = document.createElement('a');
      link.setAttribute('href', dataStr);
      link.setAttribute('download', fileName);
      link.click();
      sendNotification({ type: 'success', message: t('Settings exported successfully')});
    } catch (error) {
      sendNotification({type: 'error',message: t('An error occurred while exporting data')});
    }
  }, [lowSpeedData, cameraData, sendNotification, t]);

  const getLinePoints = useCallback((pointsArray: IVector2[]): ILinePoints => {
    const boundaryOffset = 0;
    let changeLineStyle = false;

    if (image === '') {
      return { points: pointsArray, changeLineStyle };
    }
  
    // Initialize an array to store the adjusted points
    const adjustedPoints = pointsArray.map(point => {
      let adjustedX = point.x;
      let adjustedY = point.y;
  
      // Check and adjust X coordinate
      if (point.x < boundaryOffset) {
        adjustedX = boundaryOffset;
        changeLineStyle = true;
      } else if (point.x > imageDetails.x - boundaryOffset) {
        adjustedX = imageDetails.x - boundaryOffset;
        changeLineStyle = true;
      }
  
      // Check and adjust Y coordinate
      if (point.y < boundaryOffset) {
        adjustedY = boundaryOffset;
        changeLineStyle = true;
      } else if (point.y > imageDetails.y - boundaryOffset) {
        adjustedY = imageDetails.y - boundaryOffset;
        changeLineStyle = true;
      }
  
      return { x: adjustedX, y: adjustedY };
    });
  
    return { points: adjustedPoints, changeLineStyle };

  }, [image, imageDetails]);

  const importSettings = useCallback((importedTrafficCounterData: IAdvancedLowSpeedDetectionData) => {
    const { advanced_low_speed_settings = [] } = importedTrafficCounterData;
    
    if (advanced_low_speed_settings.length > 0) {
      const lineInfo: IAdvancedLowSpeedDetails[] = [];
      setLowSpeedSelectOptions([NEW_SET]);
      advanced_low_speed_settings.map((item, index) => {
        const lineName = !item.line_set_name ? `Area ${index}` : item.line_set_name;

        setLowSpeedSelectOptions(prev => [...prev, lineName]);

        const line1Point = cloneDeep(item.lines.line1);
        const line2Point = cloneDeep(item.lines.line2);

        const { points: points1, changeLineStyle: changeLineStyle1 } = getLinePoints(line1Point);
        const { points: points2, changeLineStyle: changeLineStyle2 } = getLinePoints(line2Point);

        lineInfo.push(
          {
            index: item.index,
            line_set_name: item.line_set_name,
            styling: changeLineStyle1 || changeLineStyle2 ? 'danger' : item.styling,
            lines: {
              line1: points1,
              line2: points2,
              line1_styling: changeLineStyle1 ? 'danger' : item.styling,
              line2_styling: changeLineStyle2 ? 'danger' : item.styling
            },
            direction: item.direction,
            distance: item.distance,
            level1_speed_decrease_threshold: item.level1_speed_decrease_threshold,
            level2_speed_decrease_threshold: item.level2_speed_decrease_threshold,
            level1_enabled: item.level1_enabled,
            level2_enabled: item.level2_enabled
          }
        );
        return {
          name: index + 1 + ': ' + lineName,
          points: points1.concat(points2),
          readOnly: true,
          styling: changeLineStyle1 || changeLineStyle2 ? 'danger' : 'primary',
          showSmallDirectionMark: true
        };
      });
      setLowSpeedSetsSettings(lineInfo);
      setLowSpeedData({enabled: lowSpeedData.enabled, advanced_low_speed_settings: lineInfo});
    } else {
      setLowSpeedSetsSettings([]);
      setLowSpeedData({enabled: lowSpeedData.enabled, advanced_low_speed_settings: []});
    }
  }, [lowSpeedData, getLinePoints]);

  const validateImporting = useCallback((importedTrafficCounterData) => {
    importedTrafficCounterData.advanced_low_speed.forEach((item: { lines: any; name: any; line_set_name: string; distance: number; distance_meters: any; level1_speed_decrease_threshold: number; speed_decrease_level1_percent: any; level2_speed_decrease_threshold: number; speed_decrease_level2_percent: any; level1_enabled: boolean; compare_speed_to_last_hour: any; level2_enabled: boolean; compare_speed_to_last_5min: any; index: number; }, index: number) => {
      const { lines, distance_meters, speed_decrease_level1_percent, speed_decrease_level2_percent, compare_speed_to_last_hour, compare_speed_to_last_5min } = item;
      item.index = index + 1;
      item.line_set_name = item.name;
      item.lines = {
        line1: [
          {
            x: lines[0].x0,
            y: lines[0].y0,
          },
          {
            x: lines[0].x1,
            y: lines[0].y1,
          }
        ],
        line2: [
          {
            x: lines[1].x0,
            y: lines[1].y0,
          },
          {
            x: lines[1].x1,
            y: lines[1].y1,
          }
        ]
      };
      item.distance = distance_meters;
      item.level1_speed_decrease_threshold = speed_decrease_level1_percent;
      item.level2_speed_decrease_threshold = speed_decrease_level2_percent;
      item.level1_enabled = compare_speed_to_last_hour;
      item.level2_enabled = compare_speed_to_last_5min;
      delete item.name;
      delete item.distance_meters;
      delete item.speed_decrease_level1_percent;
      delete item.speed_decrease_level2_percent;
      delete item.compare_speed_to_last_hour;
      delete item.compare_speed_to_last_5min;
    });
  
    const newImportedData: IAdvancedLowSpeedDetectionData = {
      advanced_low_speed_settings: importedTrafficCounterData.advanced_low_speed,
      enabled: prevValuesForCompare.enabled
    };
    const updatedPrevValuesForCompare = prevValuesForCompare?.advanced_low_speed_settings.map(obj => {
      const newObj = { ...obj };
      delete newObj.styling;
      delete newObj.direction;
      return newObj;
    });
    if(isEqual(updatedPrevValuesForCompare, newImportedData.advanced_low_speed_settings)){
      handleLowSpeed();
      setModalOpen(false);
      sendNotification({type: 'success', message: t('Settings imported successfully')});
    } else {
      setSelectedSetsIndex(0);
      setSelectedSet(NEW_SET);
      setLowSpeedSetsSettings([]);
      setLowSpeedData({enabled: lowSpeedData.enabled, advanced_low_speed_settings: []});
      importSettings({...newImportedData});
      setModalOpen(false);
      sendNotification({type: 'success', message: t('Settings imported successfully')});
    }
  }, [t, sendNotification, importSettings, setModalOpen, lowSpeedData, prevValuesForCompare, handleLowSpeed]);

  const isValidImportFile = useCallback((jsonData) => {
    let isLinesValid = false;
    const validAdvLowSpeedKeys: AdvLowSpeedKeys[] = ['advanced_low_speed'];
    const validLineKeys: LineKeys[] = ['name', 'distance_meters', 'speed_decrease_level1_percent', 'speed_decrease_level2_percent', 'compare_speed_to_last_hour', 'compare_speed_to_last_5min', 'lines'];
    const validPointsKeys: PointsKeys[] = ['x0', 'y0', 'x1', 'y1'];

    try {
      let resKeys: AdvLowSpeedKeys[] = [], lineKeys: LineKeys[] = [];
      const resObj: AdvLowSpeedRes = JSON.parse(jsonData);

      resKeys = Object.keys(resObj) as Array<keyof typeof resObj>;

      if (
        resKeys.every(key => validAdvLowSpeedKeys.includes(key)) &&
        validAdvLowSpeedKeys.every(key => (resKeys.includes(key) && checkType(key, resObj[key])))
      ) {

        if (resObj.advanced_low_speed.length === 0) return true;
        if (resObj.advanced_low_speed.length > 2){
          sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Maximum 2 line sets can be imported')})));
          return false;
        }

        const isValidLineKeys = (lineKeys: LineKeys[]) => {
          if(lineKeys.every(key => validLineKeys.includes(key)) && validLineKeys.every(key => lineKeys.includes(key))){
            return true;
          } else {
            sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Invalid keys')})));
            return false;
          }
        };

        const isValidPointsKeys = (pointsKeys: PointsKeys[]) => {
          if(pointsKeys.every(key => validPointsKeys.includes(key)) && validPointsKeys.every(key => pointsKeys.includes(key))){
            return true;
          } else {
            sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Invalid keys')})));
            return false;
          }
        };

        const isValidPointsKeyTypes = (pointsKeys: PointsKeys[], point: ILinesMapped) => {
          if(pointsKeys.every((key) => checkType(key, point[key]))){
            return true;
          } else {
            sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Invalid point keys')})));
            return false;
          }
        };

        const isValidLineKeyTypes = (lineKeys: LineKeys[], line: IAdvancedLowSpeedDetailsMapped) => {
          return lineKeys.every((key) => {
            if (key === 'lines') {
              if(line[key].length > 2){
                sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Maximum 2 lines are accepted in a set')})));
                return false;
              } else {
                return true;
              }
            } else if (key === 'name') {
              if(typeof line[key] === 'string' && line[key] !== ''){
                if(line[key].length > 24){
                  sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Name should not longer than 24 characters')})));
                  return false;
                } else {
                  return true; 
                }
              } else {
                sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Name is required')})));
                return false;
              }
            } else if(key === 'distance_meters'){
              if(typeof line[key] === 'number' && !isNaN(line[key]) && line[key] <= 999.99 && line[key] >= 1){
                return true;
              } else {
                sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Line distance should be > 0 and < 1000 m')})));
                return false;
              }
            } else if(key === 'speed_decrease_level1_percent'){
              if(typeof line[key] === 'number' && !isNaN(line[key]) && line[key] <= 100 && line[key] >= 1){
                return true;
              } else {
                sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Speed decrease level 1 should be > 0 and ≤ 100')})));
                return false;
              }
            } else if(key === 'speed_decrease_level2_percent'){
              if(typeof line[key] === 'number' && !isNaN(line[key]) && line[key] <= 100 && line[key] >= 1){
                return true;
              } else {
                sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Speed decrease level 2 should be > 0 and ≤ 100')})));
                return false;
              } 
            } else {
              if(checkType(key, line[key])){
                return true;
              } else {
                sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Invalid data types')})));
                return false;
              }
            }
          });
        };

        for (const line of resObj.advanced_low_speed) {
          lineKeys = Object.keys(line) as Array<keyof typeof line>;
          if (isValidLineKeys(lineKeys) && isValidLineKeyTypes(lineKeys, line)) {
            for (const lineItem of line.lines) {
              const pointsKeys = Object.keys(lineItem) as Array<keyof typeof lineItem>;
              if (isValidPointsKeys(pointsKeys) && isValidPointsKeyTypes(pointsKeys, lineItem)) {
                isLinesValid = true;
              } else {
                sendNotification(JSON.parse(JSON.stringify({ type: 'error', message: t('Invalid point keys or their type') })));
                isLinesValid = false;
                break;
              }
            }
          } else {
            isLinesValid = false;
            break;
          }
        }
        return isLinesValid;
      } else {
        sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Invalid keys')})));
        return false;
      }
    } catch (error) {
      sendNotification(JSON.parse(JSON.stringify({type: 'error', message: t('Invalid file')})));
      return false;
    }
  }, [sendNotification, t]);

  const onImportCallback = useCallback((e) => {
    if (e !== null && isValidImportFile(e.result)) {
      validateImporting(JSON.parse(e.result as string));
    } 
    else {
      setModalOpen(false);
    }
  }, [isValidImportFile, validateImporting, setModalOpen]);

  // Import Modal
  const getImportModal = useCallback(() => {
    return (
      <ImportSettingsModal {...{ onImportCallback, sendNotification }} />
    );
  }, [sendNotification, onImportCallback]);

  const onImportSettings = useCallback(() => {
    setModalOpen(true);
    const importModal: ReactElement = getImportModal();
    createModal({
      isCloseEnable: false,
      width: 'max-length',
      padding: false,
      customComponent: importModal,
    });
  }, [createModal, setModalOpen, getImportModal]);

  const getCameraImage = useCallback(async () => {
    try {
      const res: IImageResponse = await axios.get(`${EDGE_API_BASE_URL}stacks/${streamName}/snapshot?timestamp=${Date.now()}`, { responseType: 'arraybuffer' });
      if (res.status === 200 && res.data) {
        const imgBase64 = 'data:image/jpg;base64,' + Buffer.from(res.data).toString('base64');
        const isImageValid = await isMediaUrlValid(imgBase64, 'img');
        if (isImageValid === true) {
          setImage(imgBase64);
          const img = new Image();
          img.src = imgBase64;
          setImageDetails({x: img.width, y: img.height});
        }
        setIsImporting(false);
      }
    } catch (err) {
      console.error(err);
    }
  }, [streamName, isMediaUrlValid]);

  useEffect(() => {
    if(isImporting){
      getCameraImage();
    }
  },[isImporting, getCameraImage]);

  return (
    <MainContainer>
      <Container>
        <Header>
          <PageHeaderDiv>
            <AreaContainer onClick={goToPreviousPage}>{cameraData?.camera_name}</AreaContainer>
            <HeadTitle>
              <PageHeader title={tRef.current('Advanced - Low Speed')} icon='Analyse' updateDocTitle={false} />
            </HeadTitle>
            <ToggleDetection state={toggleAnalysis ? 'disabled' : 'default'}>
              <Switch checked={lowSpeedData?.enabled} onChangeCallback={handleAnalysis} state={toggleAnalysis ? 'disabled' : 'default'} />
              <SwitchLabel>{lowSpeedData?.enabled ? tRef.current('Enabled') : tRef.current('Disabled')}</SwitchLabel>
            </ToggleDetection>
          </PageHeaderDiv>
          <ButtonContainer>
            <Button size='small' design='secondary' onClick={goToPreviousPage}> {unSavedChanges === 0 ? t('Back') : t('Cancel')} </Button>
            <ButtonWithLoading size='small' loading={lowSpeedSaveLoading} disabled={isEqual(lowSpeedData.advanced_low_speed_settings, prevValuesForCompare.advanced_low_speed_settings) ?  true : false} onClick={openConfirmationModal}> {tRef.current('Save Changes')} </ButtonWithLoading>
            {unSavedChanges > 0 ? <UnsavedChangesContainer>{tRef.current('{X} unsaved changes').replace('{X}', (unSavedChanges+ ''))}</UnsavedChangesContainer> : <UnsavedChangesContainer>{tRef.current('No Changes')}</UnsavedChangesContainer>}
          </ButtonContainer>
        </Header>

        <Divider />

        <ImportExportContainer gap='0 14px'>
          {t('Settings')}:
          <Button size='xsmall' design='secondary' disabled={false} onClick={onImportSettings}>
            {t('Import')}
          </Button>
          <Button size='xsmall' design='secondary' disabled={false} onClick={onExportSettings}>
            {t('Export')}
          </Button>
        </ImportExportContainer>
        <Divider />
        <LineSettingsContainer>
          <LineSelectFieldContainer>
            <Icon icon='FeatureLineUi' size={16} color='dimmed' />
            <StyledSelectField disabled={false} isCompact changeCallback={(e) => onLineSelect(e)} value={selectedSet}>
              {
                lowSpeedSelectOptions.map((item, index) => {
                  return (
                    <option key={item + index} value={item === NEW_SET ? NEW_SET : `${item}`}>
                      {item === NEW_SET ? tRef.current(NEW_SET) : `${index}: ${item}`}
                    </option>
                  );
                })
              }
            </StyledSelectField>
            <InputAndButtonContainer lang={i18n.language} gap='0 10px'>
              <ButtonWithIcon icon='Add' position='left' size='small' design='secondary' disabled={!isMediaLoaded || lowSpeedData?.advanced_low_speed_settings.length === 2 || selectedSet !== NEW_SET} onClick={onAddSets}>{tRef.current('Add Set')}</ButtonWithIcon>
              <ButtonWithIcon icon='ActionRemove' position='left' size='small' design='secondary' disabled={selectedSet === NEW_SET} onClick={onRemoveSets}>{tRef.current('Remove Set')}</ButtonWithIcon>
            </InputAndButtonContainer>
          </LineSelectFieldContainer>
          <LineUIContainer>
            <LineUILeftPanel>
              <SidePaddingDiv>
                <StyledSmallInputWithLabel
                  fieldState='default'
                  label={tRef.current('Line Set Reference')}
                  name='setReference'
                  id='setReference'
                  length='100%'
                  maxLength={24}
                  onChange={onChangeSets('lineReference')}
                  value={selectedSetsIndex === 0 ? '' : lowSpeedData?.advanced_low_speed_settings[selectedSetsIndex - 1]?.line_set_name || ''}
                  disabled={selectedSet === NEW_SET}
                />
              </SidePaddingDiv>
              <DividerWithMargin />
              <InputDiv>
                <SmallInput 
                  type='number' 
                  name='distance' 
                  id='distance' 
                  onChange={onChangeSets('distance')} 
                  onKeyDown={handleKeyDownDistance}
                  label={tRef.current('Line Distance')} 
                  unit={tRef.current('m')} 
                  step={10}
                  min={10}
                  value={selectedSetsIndex === 0 ? '' : lowSpeedData?.advanced_low_speed_settings[selectedSetsIndex - 1]?.distance || ''}
                  disabled={selectedSet === NEW_SET}
                />
              </InputDiv>
              <DecreaseLavel>{t('Speed Decrease Levels')}</DecreaseLavel>
              <LowSpeedThreshold>
                <SmallInput 
                  type='number' 
                  name='level1' 
                  id='level1' 
                  onChange={onChangeSets('level1')} 
                  label={tRef.current('Level 1')} 
                  onKeyDown={handleKeyDownLevel}
                  unit='%' 
                  max='100'
                  maxLength={5}
                  step={10}
                  value={selectedSetsIndex === 0 ? '' : lowSpeedData?.advanced_low_speed_settings[selectedSetsIndex - 1]?.level1_speed_decrease_threshold || ''}
                  disabled={selectedSet === NEW_SET}
                />
                <SmallInput 
                  type='number' 
                  name='level2' 
                  id='level2' 
                  onChange={onChangeSets('level2')} 
                  label={tRef.current('Level 2')} 
                  onKeyDown={handleKeyDownLevel}
                  unit='%' 
                  max='100'
                  maxLength={5}
                  step={10}
                  value={selectedSetsIndex === 0 ? '' : lowSpeedData?.advanced_low_speed_settings[selectedSetsIndex - 1]?.level2_speed_decrease_threshold || ''}
                  disabled={selectedSet === NEW_SET}
                />
              </LowSpeedThreshold>
              <DecreaseLavel>{t('Compare Speed To')}:</DecreaseLavel>
              <CompareSpeedContainer lang={i18n.language}>
                <CompareSpeed>
                  <SwitchLabel>{tRef.current('Last Hour')}</SwitchLabel>
                  <SwitchContainer><Switch state={selectedSet !== NEW_SET ? 'default':'disabled'} checked={selectedSetsIndex === 0 ? false : lowSpeedData?.advanced_low_speed_settings[selectedSetsIndex - 1]?.level1_enabled} onChangeCallback={handleLastHour} /></SwitchContainer>
                </CompareSpeed>
                <CompareSpeed>
                  <SwitchLabel>{tRef.current('Last 5 mins')}</SwitchLabel>
                  <SwitchContainer><Switch state={selectedSet !== NEW_SET ? 'default':'disabled'} checked={selectedSetsIndex === 0 ? false : lowSpeedData?.advanced_low_speed_settings[selectedSetsIndex - 1]?.level2_enabled} onChangeCallback={handleLastFiveHour} /></SwitchContainer>
                </CompareSpeed>
              </CompareSpeedContainer>
            </LineUILeftPanel>
            <LineUIRightPanel>
              <LineViewer
                streamName={streamName}
                linesData={setOfLines}
                showExclusionArea={(exclusionAreas.enabled && exclusionAreas.areas.length > 0)}
                showExclusionAreaOnModal={(exclusionAreas.enabled && exclusionAreas.areas.length > 0)}
                exclusionAreas={(exclusionAreas.enabled && exclusionAreas.areas.length > 0) ? exclusionAreas.areas : []}
                options={options}
                latestSnapshotText={tRef.current('Latest Snapshot')}
                rtcText={tRef.current('Live Feed')}
                zoomText={tRef.current('Zoom')}
                selectedLine={selectedSet}
                onLineSelect={onLineSelect}
                onLineChangeCallback={onLineChange}
                onMediaLoadedCallback={onMediaLoadedCallback}
                onLineClickCallback={onLineClickCallback}
                fromExclusionPage
              />
            </LineUIRightPanel>
            {isMediaLoaded && lowSpeedData?.advanced_low_speed_settings.length !== 0 &&
              <LineUIActionsDisplayDropdown>
                <IconWrapper>
                  <Icon icon='PasswordShow' size={16} color='dimmed' />
                </IconWrapper>
                <SelectZoneShow changeCallback={(e) => handleZoneShow(e)} disabled={false} isCompact value={selectZoneOption}>
                  {['Only Active Set', 'Show All'].map((item, index) => {
                    return (
                      <option key={index} value={item}>{tRef.current(item)}</option>);
                  })}
                </SelectZoneShow>
          
              </LineUIActionsDisplayDropdown>}
          </LineUIContainer>
        </LineSettingsContainer>
      </Container>

    </MainContainer>
  );
};

export default AdvancedLowSpeed;